'use client'

import {
  AspectRatio,
  Box,
  Flex,
  HStack,
  SimpleGrid,
  Text,
  VStack
} from '@chakra-ui/react'
import React from 'react'
import { Link as ScrollLink } from 'react-scroll'

import Image from 'next/image'
import { LocationIcon } from '@icons/location-icon'
import TriangleCenterLineImage from '@images/brands/cheongdam/triangle-center-line.png'
import Link from 'next/link'

interface Outlets {
  location: string
  stores: string[]
}

const Outlets: Outlets[] = [
  {
    location: 'Guardian - Central',
    stores: [
      'Blk 449 Ang Mo Kio Ave 10 #01-1729',
      'Paragon #B1-20/24',
      'Blk 704 Ang Mo Kio Ave 8 #01-2559C',
      'Plaza Singapura #B2-15',
      'Ang Mo Kio Hub #B2-01/02',
      'Raffles City #B1-01',
      'Blk 410 Ang Mo Kio #01-847',
      'Raffles Place MRT #B1-37-40/43-45',
      'Blk 512 Bishan St 13 #01-524',
      'Suntec City #02-705/707',
      'Chinatown Point #B1-34',
      'Takashimaya #B2-15-20',
      'City Square Mall #B1-18/19',
      'Tanjong Pagar MRT #B2-01/02/03',
      'Citylink Mall #B1-67',
      'The Centrepoint #B1-01/02/03/04',
      'Clarke Quay Central #01-55/56/57',
      'The Woodleigh Mall #B1-36',
      'Compass One #B1-11',
      'Tiong Bahru MRT Station #B1-01',
      'Great World #B1-122/123/124/125',
      'Tiong Bahru Plaza #B1-125',
      'Greenwich Village, 1 Seletar Road, #01-02/16',
      'Blk 190 Toa Payoh Lorong 6 #01-578',
      'Holland Shopping Centre #01-10/11',
      'Blk 520 Toa Payoh Lorong 6 #01-58',
      'Ion Orchard #B4-02',
      'United Square #B1-28',
      'Novena Square #01-31'
    ]
  },
  {
    location: 'Guardian - North East',
    stores: [
      'Blk 744 Bedok Reservoir Road #01-3063',
      'Blk 152 Serangoon North Ave 1 #01-338',
      'Blk 632 Bedok Reservoir Road #01-812',
      'Our Tampines Hub #B1-29',
      'Century Square #B1-01 ',
      'Blk 201E Tampines St 21 #01-94',
      'Blk 205 Hougang Street 21 #01-129/131',
      'Tampines MRT #01-01',
      'Hougang Mall #B1-31',
      'Waterway Point #B1-04',
      'Northshore Plaza II #01-06/07/08',
      'White Sands Mall #01-12',
      'Oasis Terrace #01-22/23/24/25'
    ]
  },
  {
    location: 'Guardian - North West',
    stores: [
      'Causeway Point #B1-28/29/30',
      'Blk 678A Woodlands Ave 6 #01-13',
      'North Point North Wing #B1-02/03',
      'Blk 888 Woodland Dr 50 #01-731/731A',
      'Sun Plaza #B1-11'
    ]
  },
  {
    location: 'Guardian - South East',
    stores: [
      'Aljunied MRT #01-14/15',
      'Blk 39 Jalan Tiga #01-02',
      '18 Tai Seng Street #01-19/20/21',
      'Kallang Leisure Park #01-23',
      'Blk 113 Aljunied Ave 2 #01-27',
      'Kallang Wave Mall #01-51',
      'Bedok Mall #B2-45/46/47',
      'Macpherson MRT #B2-02',
      'Blk 210 New Upper Changi Road #01-715',
      'Parkway Parade #B1-147/149',
      'Eastpoint Mall #02-01/02/35',
      'Paya Lebar Quarter #B2-01/35',
      'i12 Katong #B1-15/16',
      'Serangoon Nex #B1-62/63'
    ]
  },
  {
    location: 'Guardian - South West',
    stores: [
      'Beauty World MRT #B1-02',
      'Lot 1 #B1-26/27',
      'Harbourfront Centre #02-111/112',
      'Star Vista #B1-16',
      'JEM #B1-16/17',
      'Sunshine Place Shopping Centre #01-11',
      'Jurong Point B1-27/28',
      'Vivo #02-79',
      'Blk 503 Jurong West Ave 1 #01-845',
      'Westgate #B1-20',
      'Lakeside MRT #B1-02'
    ]
  }
]

const OutletsLocation = ({ outlet }: { outlet: Outlets }) => {
  return (
    <Box data-aos="fade-up" mb={8} mt={3}>
      <Text textStyle={'outletTitle'} w={'100%'}>
        {outlet.location}
      </Text>
      <SimpleGrid columns={{ base: 1, sm: 2 }} gap={6} w={'100%'} mt={5}>
        {outlet.stores.map((store) => {
          return (
            <HStack key={store}>
              <Box>
                <LocationIcon width={20} height={20} />
              </Box>
              <Text textStyle={'outletText'}>{store}</Text>
            </HStack>
          )
        })}
      </SimpleGrid>
    </Box>
  )
}

const ParticipatingOutlets = () => {
  return (
    <Box maxW={700} mx="auto" mt={8} mb={20}>
      <VStack data-aos="fade-up" alignItems={'center'} gap={8}>
        <Text textAlign={'center'} textStyle={'exclusiveTitle'}>
          Participating outlets
        </Text>
        <Image alt="" src={TriangleCenterLineImage} width={624} height={200} />
      </VStack>
      <Flex
        direction={{ base: 'column', sm: 'row' }}
        justify="space-between" //
        align={{ base: 'center', sm: 'center' }}
        gap={6}
        w="50%"
        mt={5}
      >
        <Box
          data-aos="fade-up"
          flex={1}
          w={{
            base: '60%',
            sm: 'auto'
          }}
          cursor="pointer"
        >
          <ScrollLink
            to="guardian"
            smooth={true}
            duration={500}
            offset={-180} // Adjusted for header height
          >
            <AspectRatio ratio={1366 / 768} position="relative">
              <Image
                src={
                  'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/guardian-1713812201249.png'
                }
                alt="guardian"
                fill
              />
            </AspectRatio>
          </ScrollLink>
        </Box>
        <Box
          data-aos="fade-up"
          cursor="pointer"
          w={{
            base: '60%',
            sm: '90px',
            md: '100px'
          }}
        >
          <AspectRatio
            w={{
              base: '100%',
              sm: '90px',
              md: '100px'
            }}
            ratio={1 / 1}
          >
            <Link href="https://qrs.ly/kxfr4ki" target="_blank">
              <Image
                src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/wonder-1711598628576.png"
                fill
                alt="wonder"
              />
            </Link>
          </AspectRatio>
        </Box>
      </Flex>
      <VStack
        data-aos="fade-up"
        mt={20}
        w={'100%'}
        alignItems={'start'}
        id="guardian"
      >
        <Text textAlign={'left'} textStyle={'participateTitle'}>
          Guardian
        </Text>
      </VStack>
      {Outlets.map((outlet, i) => {
        return <OutletsLocation key={i} outlet={outlet} />
      })}
    </Box>
  )
}

export default ParticipatingOutlets
